import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

import { st, Modal, MainView, Form, FormControl, FormSection, Button, Grid, Link, ReviewDecorator } from "lib/index";
import { stateSelector, close, fetchReview, updateReview, submit, removeReview } from "./index";

@connect(
  (state) => ({
    opened: stateSelector(state).opened,
    reviewId: stateSelector(state).reviewId,
    review: stateSelector(state).form.model,
    fetchDone: stateSelector(state).review.fetchDone,
    fetchLoading: stateSelector(state).review.fetchLoading,
    fetchFailed: stateSelector(state).review.fetchFailed,
    errors: stateSelector(state).form.errors,
    valid: stateSelector(state).form.valid,
    submitLoading: stateSelector(state).form.submitLoading || stateSelector(state).removal.fetchLoading,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        close,
        fetchReview,
        updateReview,
        submit,
        removeReview,
      },
      dispatch
    )
)
export default class EditReviewModal extends React.PureComponent {
  handleSubmit(event) {
    event.preventDefault();
    this.props.submit();
  }

  handlePropChange(prop, value) {
    const { review, updateReview } = this.props;
    const nextReview = new ReviewDecorator({
      ...review,
      [prop]: value,
    });
    updateReview(nextReview);
  }

  handleRemoveClick() {
    const { removeReview } = this.props;
    st.confirm({
      content: I18n.t("papp.reviews.EditReviewModal.remove_confirm_message"),
    }).then((yes) => yes && removeReview());
  }

  render() {
    const {
      opened,
      reviewId,
      fetchLoading,
      fetchFailed,
      fetchDone,
      fetchReview,
      close,
      review,
      valid,
      errors,
      validate,
      submit,
      submitLoading,
    } = this.props;
    const canUpdate = !!review && st.authorize("update", review);
    return (
      opened && (
        <Modal closeIcon closeOnDimmerClick={false} open onClose={() => close()}>
          <Modal.Header>{I18n.t("papp.reviews.EditReviewModal.title")}</Modal.Header>
          <Modal.Content>
            <MainView.Dimmer loading={fetchLoading} failed={fetchFailed} retry={fetchReview} />
            {fetchDone && review && (
              <Form onSubmit={this.handleSubmit.bind(this)} error={!valid} loading={submitLoading}>
                {review.product_name && (
                  <FormControl.FormGroup label={I18n.humanAttributeName("review", "product")} inline>
                    <span className="u-marginLeft">{review.product_name}</span>
                  </FormControl.FormGroup>
                )}

                {review.order_id && (
                  <FormControl.FormGroup label={I18n.humanAttributeName("review", "order")} inline>
                    <span className="u-marginLeft">
                      <Link to={`/c/${st.authorize.company.id}/orders/${review.order_id}/${review.order_token}`}>
                        #{review.order_id}
                      </Link>
                    </span>
                  </FormControl.FormGroup>
                )}

                <FormControl.MultipleRadioFormGroup
                  {...FormControl.addProps(`review.rating`, { errors })}
                  value={review.rating}
                  onChange={this.handlePropChange.bind(this, "rating")}
                  onBlur={validate}
                  label={I18n.humanAttributeName("review", "rating")}
                  required
                  options={[1, 2, 3, 4, 5]}
                  inlineLabel
                  inline
                  readOnly={!canUpdate}
                />

                <FormControl.TextAreaFormGroup
                  {...FormControl.addProps(`review.content`, { errors })}
                  value={review.content}
                  onChange={this.handlePropChange.bind(this, "content")}
                  onBlur={validate}
                  label={I18n.humanAttributeName("review", "content")}
                  autoHeight
                  readOnly={!canUpdate}
                />

                <FormControl.MultipleRadioFormGroup
                  {...FormControl.addProps(`review.st_rating`, { errors })}
                  value={review.st_rating}
                  onChange={this.handlePropChange.bind(this, "st_rating")}
                  onBlur={validate}
                  label={I18n.humanAttributeName("review", "st_rating")}
                  required
                  options={[1, 2, 3, 4, 5]}
                  inlineLabel
                  inline
                  readOnly={!canUpdate}
                />

                <FormControl.TextAreaFormGroup
                  {...FormControl.addProps(`review.st_feedback`, { errors })}
                  value={review.st_feedback}
                  onChange={this.handlePropChange.bind(this, "st_feedback")}
                  onBlur={validate}
                  label={I18n.humanAttributeName("review", "st_feedback")}
                  autoHeight
                  readOnly={!canUpdate}
                />

                <Grid columns={2}>
                  <Grid.Column>
                    <FormControl.InputFormGroup
                      {...FormControl.addProps(`review.author_name`, {
                        errors,
                      })}
                      value={review.author_name}
                      onChange={this.handlePropChange.bind(this, "author_name")}
                      onBlur={validate}
                      label={I18n.humanAttributeName("review", "author_name")}
                      inlineLabel
                      readOnly={!canUpdate}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FormControl.InputFormGroup
                      {...FormControl.addProps(`review.author_email`, {
                        errors,
                      })}
                      value={review.author_email}
                      onChange={this.handlePropChange.bind(this, "author_email")}
                      onBlur={validate}
                      label={I18n.humanAttributeName("review", "author_email")}
                      type="email"
                      required
                      inlineLabel
                      readOnly={!canUpdate}
                    />
                  </Grid.Column>
                </Grid>

                <Grid columns={3} className="u-marginBottom">
                  <Grid.Column>
                    <FormControl.LocaleSelectFormGroup
                      {...FormControl.addProps(`review.locale`, { errors })}
                      value={review.locale}
                      onChange={this.handlePropChange.bind(this, "locale")}
                      onBlur={validate}
                      label={I18n.humanAttributeName("review", "locale")}
                      includeBlank
                      inlineLabel
                      className="u-smallWidth"
                      readOnly={!canUpdate}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FormControl.SelectFormGroup
                      {...FormControl.addProps(`review.age_group`, { errors })}
                      value={review.age_group}
                      onChange={this.handlePropChange.bind(this, "age_group")}
                      onBlur={validate}
                      label={I18n.humanAttributeName("review", "age_group")}
                      includeBlank
                      options={ReviewDecorator.age_group_options}
                      inlineLabel
                      className="u-smallWidth"
                      readOnly={!canUpdate}
                    />
                  </Grid.Column>
                  <Grid.Column>
                    <FormControl.SelectFormGroup
                      {...FormControl.addProps(`review.group_type`, { errors })}
                      value={review.group_type}
                      onChange={this.handlePropChange.bind(this, "group_type")}
                      onBlur={validate}
                      label={I18n.humanAttributeName("review", "group_type")}
                      includeBlank
                      options={ReviewDecorator.group_type_options}
                      inlineLabel
                      className="u-smallWidth"
                      readOnly={!canUpdate}
                    />
                  </Grid.Column>
                </Grid>

                <FormControl.CheckboxFormGroup
                  {...FormControl.addProps(`review.verified`, { errors })}
                  value={review.verified}
                  onChange={this.handlePropChange.bind(this, "verified")}
                  onBlur={validate}
                  label={I18n.humanAttributeName("review", "verified")}
                  toggle
                  readOnly={!canUpdate}
                />

                <FormControl.Checkbox
                  label={I18n.humanAttributeName("order", "published")}
                  value={review.visible}
                  readOnly={!canUpdate}
                  onChange={this.handlePropChange.bind(this, "visible")}
                  toggle
                />

                <button type="submit" disabled={submitLoading} hidden />
              </Form>
            )}
          </Modal.Content>
          {fetchDone && review && (
            <Modal.Actions>
              <Button as="a" floated="left" basic onClick={() => close()} content={I18n.t("papp.common.go_back")} />
              {!!review.id && st.authorize("destroy", review) && (
                <Button
                  as="a"
                  floated="left"
                  basic
                  negative
                  onClick={this.handleRemoveClick.bind(this)}
                  content={I18n.t("papp.common.remove")}
                />
              )}
              {canUpdate && (
                <Button
                  as="a"
                  primary
                  onClick={submit}
                  disabled={!valid || submitLoading}
                  content={I18n.t("papp.common.save")}
                />
              )}
            </Modal.Actions>
          )}
        </Modal>
      )
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/reviews/EditReviewModal/EditReviewModal.jsx